/**
 * Date-range selection. Every graph type receives the same normalized calendar and slices
 * it here, so switching type or preset range never needs another GitHub request.
 */
import { CalendarDay } from '../../github/types';
import { formatNumber } from './escape';
import { addDays, addMonths, formatShortDate } from './dates';
import { GraphOptions, RangeName } from '../options';
import { GraphData, PreparedData } from './types';

export const RANGE_LABELS: Record<RangeName, string> = {
    '7d': 'Last 7 days',
    '30d': 'Last 30 days',
    '90d': 'Last 90 days',
    '6m': 'Last 6 months',
    '1y': 'Last year',
    custom: 'Custom',
};

const RANGE_PHRASES: Record<Exclude<RangeName, 'custom'>, string> = {
    '7d': 'in the last 7 days',
    '30d': 'in the last 30 days',
    '90d': 'in the last 90 days',
    '6m': 'in the last 6 months',
    '1y': 'in the last year',
};

/** First date included for a preset range ending on `end` (inclusive). */
const presetStart = (range: Exclude<RangeName, 'custom' | '1y'>, end: string): string => {
    switch (range) {
        case '7d':
            return addDays(end, -6);
        case '30d':
            return addDays(end, -29);
        case '90d':
            return addDays(end, -89);
        case '6m':
            return addDays(addMonths(end, -6), 1);
    }
};

export const selectDays = (days: CalendarDay[], options: GraphOptions): CalendarDay[] => {
    if (days.length === 0) return days;
    if (options.range === 'custom')
        return days.filter((day) => day.date >= options.from && day.date <= options.to);
    if (options.range === '1y') return days;
    const start = presetStart(options.range, days[days.length - 1].date);
    return days.filter((day) => day.date >= start);
};

/**
 * Whether a calendar already contains a custom window. Days after the calendar's last date
 * don't exist yet on GitHub, so only the start matters.
 */
export const coversWindow = (days: CalendarDay[], from: string): boolean =>
    days.length > 0 && days[0].date <= from;

export const rangePhrase = (options: GraphOptions): string =>
    options.range === 'custom'
        ? `from ${formatShortDate(options.from)} to ${formatShortDate(options.to)}`
        : RANGE_PHRASES[options.range];

export const prepareData = (data: GraphData, options: GraphOptions): PreparedData => {
    const days = selectDays(data.days, options);
    // For the default window keep GitHub's own total; otherwise sum what is shown.
    const total =
        options.range === '1y' ? data.total : days.reduce((sum, day) => sum + day.count, 0);
    return {
        username: data.username,
        name: data.name ?? null,
        days,
        total,
        totalText: `${formatNumber(total)} contribution${total === 1 ? '' : 's'} ${rangePhrase(options)}`,
    };
};
